import {
    BadgeQuestionMark,
    BarChart3,
    BookOpen,
    CalendarCheck,
    ClipboardCheck,
    ExternalLink,
    FlaskConical,
    Flame,
    Headphones,
    KeyRound,
    Megaphone,
    MessageSquarePlus,
    Settings,
    Map,
    ShieldCheck,
    Ticket,
    UserCircle,
    UserX,
    Wrench,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { UserLinks } from '../../components/UserLinks';
import { useFormatUtils } from '../../hooks/useFormatUtils';
import { useDashboardData } from './hooks/useDashboardData';
import type { FeedbackData } from '../../types/feedback';
import type { ObsessLessUser } from '../../types/commonTypes';
import type { DeletionHealthCounts } from '../AccountDeletions/types/accountDeletionTypes';
import type { UserTriggerLogStats } from '../Stats/services/statsService';
import type { ReferralCodeData } from '../ReferralCodes/services/referralCodesService';
import type { UserGrowthMonthData } from './services/dashboardService';

type NavCard = {
    title: string;
    description: string;
    href: string;
    icon: React.ReactNode;
    color: string;
    hoverColor: string;
};

type NavSection = {
    label: string;
    cards: NavCard[];
};

// The production API does not serve the eval routes, nor the evals MCP server and its tokens.
const EVALS_ENABLED = import.meta.env.VITE_ENVIRONMENT_NAME !== 'production';

// ─── Shared widget shell ───────────────────────────────────────────────────────

interface WidgetCardProps {
    title: string;
    viewAllHref: string;
    loading: boolean;
    error: string | null;
    children: React.ReactNode;
}

const WidgetCard = ({ title, viewAllHref, loading, error, children }: WidgetCardProps) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
            <Link
                to={viewAllHref}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors"
            >
                View all <ExternalLink size={11} />
            </Link>
        </div>
        <div className="flex-1 px-4 py-3">
            {loading && (
                <div className="flex items-center justify-center py-6">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500" />
                </div>
            )}
            {!loading && error && (
                <p className="text-xs text-red-500 py-4 text-center">{error}</p>
            )}
            {!loading && !error && children}
        </div>
    </div>
);

// ─── Widget: Latest Feedbacks ─────────────────────────────────────────────────

const LatestFeedbacksWidget = ({
    data,
    loading,
    error,
}: {
    data: FeedbackData[];
    loading: boolean;
    error: string | null;
}) => {
    const { formatDate } = useFormatUtils();

    return (
        <WidgetCard title="Latest User Feedbacks" viewAllHref="/feedback" loading={loading} error={error}>
            {data.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">No feedback yet.</p>
            ) : (
                <ul className="space-y-3">
                    {data.map((fb) => (
                        <li key={fb.id} className="space-y-1">
                            <p className="text-xs text-gray-500 line-clamp-2">{fb.text}</p>
                            <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-1.5 min-w-0">
                                    <span className="text-xs text-gray-400 truncate">
                                        {fb.userEmail ?? 'Anonymous'}
                                    </span>
                                    {fb.userId && <UserLinks userId={fb.userId} mode="compact" />}
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    {fb.source && (
                                        <span className="inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-gray-100 text-gray-600">
                                            {fb.source}
                                        </span>
                                    )}
                                    <span className="text-[10px] text-gray-400 whitespace-nowrap">
                                        {formatDate(fb.createdAt ?? null)}
                                    </span>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </WidgetCard>
    );
};

// ─── Widget: Top Quest Journey Users ─────────────────────────────────────────

const TopQuestUsersWidget = ({
    data,
    loading,
    error,
}: {
    data: ObsessLessUser[];
    loading: boolean;
    error: string | null;
}) => (
    <WidgetCard title="Top Quest Journey Users" viewAllHref="/quests?tab=journey-leaderboard" loading={loading} error={error}>
        {data.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">No journey data yet.</p>
        ) : (
            <ul className="space-y-3">
                {data.map((user) => (
                    <li key={user.uid} className="space-y-1">
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1 min-w-0">
                                <span className="text-xs font-medium text-gray-700 truncate flex items-center gap-1">
                                    {user.email ?? user.uid}
                                </span>
                                <UserLinks userId={user.uid} mode="compact" />
                            </div>

                            <span className="shrink-0 inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-blue-100 text-blue-700">
                                Day {user.questJourney?.currentDay ?? '?'}
                            </span>
                        </div>
                        <div className="flex items-center justify-end gap-2">
                            {user.questJourney?.journeyDayStartedAt && (
                                <span className="text-[10px] text-gray-400 whitespace-nowrap">
                                    Started{' '}
                                    {user.questJourney.journeyDayStartedAt.toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                    })}
                                </span>
                            )}
                        </div>
                    </li>
                ))}
            </ul>
        )}
    </WidgetCard>
);

// ─── Widget: Top Trigger Log Users ───────────────────────────────────────────

const TopTriggerUsersWidget = ({
    data,
    loading,
    error,
}: {
    data: UserTriggerLogStats[];
    loading: boolean;
    error: string | null;
}) => (
    <WidgetCard title="Top Trigger Log Users" viewAllHref="/stats" loading={loading} error={error}>
        {data.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">No trigger log data yet.</p>
        ) : (
            <ul className="space-y-3">
                {data.map((user) => (
                    <li key={user.userId} className="space-y-1">
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1 min-w-0">
                                <span className="text-xs font-medium text-gray-700 truncate">
                                    {user.email ?? user.userId}
                                </span>
                                <UserLinks userId={user.userId} mode="compact" />
                            </div>
                            <span className="shrink-0 inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-purple-100 text-purple-700">
                                {user.triggerLogCount} logs
                            </span>
                        </div>
                    </li>
                ))}
            </ul>
        )}
    </WidgetCard>
);

// ─── Widget: User Growth (3 months) ──────────────────────────────────────────

const UserGrowthWidget = ({
    data,
    loading,
    error,
}: {
    data: UserGrowthMonthData[];
    loading: boolean;
    error: string | null;
}) => {
    const maxCount = Math.max(...data.map((d) => d.count), 1);

    return (
        <WidgetCard title="User Growth (Last 3 Months)" viewAllHref="/stats?tab=user-growth" loading={loading} error={error}>
            {data.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">No growth data yet.</p>
            ) : (
                <ul className="space-y-3">
                    {data.map((item) => (
                        <li key={item.month} className="space-y-1">
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-600">{item.displayMonth}</span>
                                <span className="text-xs font-semibold text-gray-800">
                                    {item.count.toLocaleString()} new
                                </span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-2">
                                <div
                                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${Math.max((item.count / maxCount) * 100, 4)}%` }}
                                />
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </WidgetCard>
    );
};

// ─── Widget: Latest Updated Referral Codes ────────────────────────────────────

const LatestReferralCodesWidget = ({
    data,
    loading,
    error,
}: {
    data: ReferralCodeData[];
    loading: boolean;
    error: string | null;
}) => (
    <WidgetCard title="Recently Updated Referral Codes" viewAllHref="/referral-codes" loading={loading} error={error}>
        {data.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">No recently updated codes.</p>
        ) : (
            <ul className="space-y-3">
                {data.map((code) => (
                    <li key={code.code} className="space-y-1">
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-mono font-semibold text-gray-800">
                                {code.code}
                            </span>
                            <span className="text-xs text-gray-400 whitespace-nowrap">{code.usesCount} uses</span>
                        </div>
                    </li>
                ))}
            </ul>
        )}
    </WidgetCard>
);

// ─── Widget: Account Deletion Health ──────────────────────────────────────────

/**
 * Deletion jobs run after the user was told "deleted", so nothing else would show a
 * failure. Failed and stalled are the two numbers worth a glance; both should be 0.
 */
const AccountDeletionsWidget = ({
    data,
    loading,
    error,
}: {
    data: DeletionHealthCounts;
    loading: boolean;
    error: string | null;
}) => {
    const rows: { label: string; value: number; hint: string; alarming: boolean }[] = [
        {
            label: 'Failed',
            value: data.failed,
            hint: 'The reconciler retries these hourly.',
            alarming: data.failed > 0,
        },
        {
            label: 'Stalled',
            value: data.stalled,
            hint: 'Unfinished and untouched for over two hours.',
            alarming: data.stalled > 0,
        },
        {
            label: 'In progress',
            value: data.inProgress,
            hint: 'Normal for a few minutes after a deletion.',
            alarming: false,
        },
    ];

    return (
        <WidgetCard
            title="Account Deletion Health"
            viewAllHref="/account-deletions"
            loading={loading}
            error={error}
        >
            <ul className="space-y-3">
                {rows.map((row) => (
                    <li key={row.label} className="flex items-center justify-between gap-2">
                        <span className="text-xs text-gray-500" title={row.hint}>
                            {row.label}
                        </span>
                        <span
                            className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${
                                row.alarming
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-gray-100 text-gray-600'
                            }`}
                        >
                            {row.value}
                        </span>
                    </li>
                ))}
            </ul>
            {(data.failed > 0 || data.stalled > 0) && (
                <Link
                    to="/account-deletions?status=attention"
                    className="mt-3 block text-xs text-red-600 hover:text-red-800"
                >
                    Review the records that need attention →
                </Link>
            )}
        </WidgetCard>
    );
};

// ─── Main Dashboard ────────────────────────────────────────────────────────────

export default function Dashboard() {
    const {
        latestFeedbacks,
        topQuestUsers,
        topTriggerUsers,
        userGrowth,
        latestReferralCodes,
        accountDeletions,
    } = useDashboardData();

    const sections: NavSection[] = [
        {
            label: 'Content',
            cards: [
                {
                    title: 'Audio Guides',
                    description: 'Manage audio guides and tools',
                    href: '/audio-guides',
                    icon: <Headphones />,
                    color: 'bg-cyan-500',
                    hoverColor: 'hover:bg-cyan-50',
                },
                {
                    title: 'Community Questions',
                    description: 'Upload and manage community questions from CSV',
                    href: '/community-questions',
                    icon: <BadgeQuestionMark />,
                    color: 'bg-indigo-500',
                    hoverColor: 'hover:bg-indigo-50',
                },
                {
                    title: 'Lessons',
                    description: 'Create, edit, and manage learning content',
                    href: '/lessons',
                    icon: <BookOpen />,
                    color: 'bg-blue-500',
                    hoverColor: 'hover:bg-blue-50',
                },
                {
                    title: 'Quests',
                    description: 'View and manage quests',
                    href: '/quests',
                    icon: <ShieldCheck />,
                    color: 'bg-red-500',
                    hoverColor: 'hover:bg-red-50',
                },
                {
                    title: 'Journey',
                    description: 'Manage journey templates and configuration',
                    href: '/journey',
                    icon: <Map />,
                    color: 'bg-emerald-500',
                    hoverColor: 'hover:bg-emerald-50',
                },
                {
                    title: 'Streaks',
                    description: 'Inspect streaks, edit freezes, and recreate test scenarios',
                    href: '/streaks',
                    icon: <Flame />,
                    color: 'bg-orange-500',
                    hoverColor: 'hover:bg-orange-50',
                },
                {
                    title: 'Daily Tasks',
                    description: 'Manage daily task templates',
                    href: '/daily-tasks',
                    icon: <CalendarCheck />,
                    color: 'bg-orange-500',
                    hoverColor: 'hover:bg-orange-50',
                },
            ],
        },
        {
            label: 'Users',
            cards: [
                {
                    title: 'Feedback Campaigns',
                    description: 'Create and manage dynamic feedback campaigns',
                    href: '/feedback-campaigns',
                    icon: <MessageSquarePlus />,
                    color: 'bg-emerald-500',
                    hoverColor: 'hover:bg-emerald-50',
                },
                {
                    title: 'Referral Codes',
                    description: 'View and manage referral codes and performance',
                    href: '/referral-codes',
                    icon: <Ticket />,
                    color: 'bg-pink-500',
                    hoverColor: 'hover:bg-pink-50',
                },
                {
                    title: 'User Feedback',
                    description: 'View and manage user feedback and analytics',
                    href: '/feedback',
                    icon: <Megaphone />,
                    color: 'bg-green-500',
                    hoverColor: 'hover:bg-green-50',
                },
                {
                    title: 'User Profile',
                    description: 'View and manage user profiles, OCD data, and trigger logs',
                    href: '/user-profile',
                    icon: <UserCircle />,
                    color: 'bg-teal-500',
                    hoverColor: 'hover:bg-teal-50',
                },
            ],
        },
        {
            label: 'Insights',
            cards: [
                {
                    title: 'Stats',
                    description: 'View user statistics and leaderboards',
                    href: '/stats',
                    icon: <BarChart3 />,
                    color: 'bg-violet-500',
                    hoverColor: 'hover:bg-violet-50',
                },
                ...(EVALS_ENABLED
                    ? [
                          {
                              title: 'Evals',
                              description: 'Run LLM eval suites against model and prompt variants, and review the results',
                              href: '/evals',
                              icon: <FlaskConical />,
                              color: 'bg-indigo-500',
                              hoverColor: 'hover:bg-indigo-50',
                          },
                      ]
                    : []),
            ],
        },
        {
            label: 'System',
            cards: [
                {
                    title: 'Settings',
                    description: 'Configure application settings',
                    href: '/settings',
                    icon: <Settings />,
                    color: 'bg-gray-500',
                    hoverColor: 'hover:bg-gray-50',
                },
                {
                    title: 'Tools',
                    description: 'Quick utilities like word and character counts',
                    href: '/tools',
                    icon: <Wrench />,
                    color: 'bg-amber-500',
                    hoverColor: 'hover:bg-amber-50',
                },
                {
                    title: 'HIPAA Compliance',
                    description: 'Track BAA readiness, blockers, and vendor coverage',
                    href: '/hipaa',
                    icon: <ClipboardCheck />,
                    color: 'bg-slate-600',
                    hoverColor: 'hover:bg-slate-50',
                },
                {
                    title: 'Account Deletions',
                    description: 'Monitor the erasure audit trail for failed or stalled deletions',
                    href: '/account-deletions',
                    icon: <UserX />,
                    color: 'bg-rose-500',
                    hoverColor: 'hover:bg-rose-50',
                },
                ...(EVALS_ENABLED
                    ? [
                          {
                              title: 'Api',
                              description: 'Tokens that let Claude Code read eval runs',
                              href: '/api',
                              icon: <KeyRound />,
                              color: 'bg-indigo-500',
                              hoverColor: 'hover:bg-indigo-50',
                          },
                      ]
                    : []),
            ],
        },
    ];

    return (
        <div className="min-h-screen bg-gray-50 py-4">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col gap-3">
                    {/* Nav Cards */}
                    {sections.map((section) => (
                        <div key={section.label}>
                            <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
                                {section.label}
                            </h2>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                                {section.cards.map((card) => (
                                    <Link
                                        key={card.href}
                                        to={card.href}
                                        title={card.description}
                                        className={`bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 p-3 group flex items-center gap-3 ${card.hoverColor}`}
                                    >
                                        <div
                                            className={`inline-flex items-center justify-center w-8 h-8 rounded-md ${card.color} text-white shrink-0`}
                                        >
                                            <span className="[&>svg]:w-4 [&>svg]:h-4">
                                                {card.icon}
                                            </span>
                                        </div>
                                        <span className="text-sm font-medium text-gray-800 group-hover:text-blue-600 transition-colors duration-200 leading-tight">
                                            {card.title}
                                        </span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    ))}

                    {/* At a Glance Widgets */}
                    <div>
                        <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
                            At a Glance
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                            <LatestFeedbacksWidget
                                data={latestFeedbacks.data}
                                loading={latestFeedbacks.loading}
                                error={latestFeedbacks.error}
                            />
                            <TopQuestUsersWidget
                                data={topQuestUsers.data}
                                loading={topQuestUsers.loading}
                                error={topQuestUsers.error}
                            />
                            <TopTriggerUsersWidget
                                data={topTriggerUsers.data}
                                loading={topTriggerUsers.loading}
                                error={topTriggerUsers.error}
                            />
                            <UserGrowthWidget
                                data={userGrowth.data}
                                loading={userGrowth.loading}
                                error={userGrowth.error}
                            />
                            <LatestReferralCodesWidget
                                data={latestReferralCodes.data}
                                loading={latestReferralCodes.loading}
                                error={latestReferralCodes.error}
                            />
                            <AccountDeletionsWidget
                                data={accountDeletions.data}
                                loading={accountDeletions.loading}
                                error={accountDeletions.error}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
